import React, { Component } from 'react';
import { connect } from 'react-redux';
import { formValueSelector } from 'redux-form';
import ReactTable from 'react-table';
import { Button, Form } from 'semantic-ui-react';
import PropTypes from 'prop-types';
import _ from 'lodash';

import { forceDirtyState } from 'State/dirtyTracking/actions';
import { caseInsensitiveFilter } from 'Common/GridHelper';
import { ReactTableColumnProperties } from 'Common/Constants';
import { CreateEditPrepWorksheetEnum } from 'Common/FieldEnum';
import { CK_KITCHEN_PREP_WORKSHEET_CREATE_EDIT_FORM_NAME } from 'Common/FormEnum';
import EditableLabel from 'Components/editableLabel/EditableLabel';
import { TableNamesEnum } from 'Common/Constants/Enumeration/TableEnum';

import * as actions from './../../state/createEditKitchenPrepWorksheet/actions';

class KitchenPrepWorksheetInfoContainer extends Component {
  reactTableRef = React.createRef();

  static propTypes = {
    onFilteredChange: PropTypes.func,
    onPageChange: PropTypes.func,
    onSortedChange: PropTypes.func,
    dataSource: PropTypes.array,
    sorted: PropTypes.array,
    filtered: PropTypes.array,
    page: PropTypes.number,
    negativeQuantityAllowed: PropTypes.bool,
    loading: PropTypes.bool,
  };

  static defaultProps = {
    onFilteredChange: () => {},
    onPageChange: () => {},
    onSortedChange: () => {},
    sorted: [],
    filtered: [],
    page: 0,
    negativeQuantityAllowed: false,
    loading: false,
    dataSource: [],
  };

  columns = [
    {
      Header: 'Item Code',
      columns: [
        {
          accessor: CreateEditPrepWorksheetEnum.ItemCode,
          ...ReactTableColumnProperties,
        },
      ],
    },
    {
      Header: 'Description',
      columns: [
        {
          accessor: CreateEditPrepWorksheetEnum.Description,
          width: 200,
          ...ReactTableColumnProperties,
        },
      ],
    },
    {
      Header: 'Ordered',
      columns: [
        {
          Header: 'Purchase Qty',
          headerStyle: { textAlign: 'right' },
          accessor: CreateEditPrepWorksheetEnum.OrderedPrimary,
          className: 'right-align',
          ...ReactTableColumnProperties,
        },
        {
          Header: 'Pack Qty',
          headerStyle: { textAlign: 'right' },
          accessor: CreateEditPrepWorksheetEnum.OrderedSecondary,
          className: 'right-align',
          ...ReactTableColumnProperties,
        },
      ],
    },
    {
      Header: 'Needed',
      columns: [
        {
          Header: 'Purchase Qty',
          headerStyle: { textAlign: 'right' },
          accessor: CreateEditPrepWorksheetEnum.NeededPrimary,
          className: 'right-align',
          ...ReactTableColumnProperties,
        },
        {
          Header: 'Pack Qty',
          headerStyle: { textAlign: 'right' },
          accessor: CreateEditPrepWorksheetEnum.NeededSecondary,
          className: 'right-align',
          ...ReactTableColumnProperties,
        },
      ],
    },
    {
      Header: 'Prepared',
      columns: [
        {
          Header: 'Purchase Qty',
          headerStyle: { textAlign: 'right' },
          accessor: CreateEditPrepWorksheetEnum.PreparedPrimary,
          className: 'right-align',
          ...ReactTableColumnProperties,
          Cell: ({ original }) => {
            return !this.props.disabled ? (
              <EditableLabel
                label={
                  original[CreateEditPrepWorksheetEnum.PreparedPrimary]
                    ? original[CreateEditPrepWorksheetEnum.PreparedPrimary]
                    : ''
                }
                labelClassName={'qtyLabel'}
                refData={original}
                inputType={'text'}
                onChange={this.handleChangePrimaryQuantity}
                disabled={this.props.disabled}
                allowEdit={!this.props.disabled}
              />
            ) : (
              <span>{original.PreparedPrimary}</span>
            );
          },
        },
        {
          Header: 'Pack Qty',
          headerStyle: { textAlign: 'right' },
          accessor: CreateEditPrepWorksheetEnum.PreparedSecondary,
          className: 'right-align',
          ...ReactTableColumnProperties,
          Cell: ({ original }) => {
            return !this.props.disabled ? (
              <EditableLabel
                label={
                  original[CreateEditPrepWorksheetEnum.PreparedSecondary]
                    ? original[CreateEditPrepWorksheetEnum.PreparedSecondary]
                    : ''
                }
                labelClassName={'qtyLabel'}
                refData={original}
                inputType={'text'}
                onChange={this.handleChangeSecondaryQuantity}
                disabled={this.props.disabled}
                allowEdit={!this.props.disabled}
              />
            ) : (
              <span>{original.PreparedSecondary}</span>
            );
          },
        },
      ],
    },
    {
      Header: 'Packaging',
      columns: [
        {
          accessor: CreateEditPrepWorksheetEnum.Packaging,
          ...ReactTableColumnProperties,
        },
      ],
    },
    {
      Header: 'Order Fulfilled',
      columns: [
        {
          Header: '',
          accessor: CreateEditPrepWorksheetEnum.OrderFulfilled,
          ...ReactTableColumnProperties,
          filterable: false,
          sortable: false,
          Cell: ({ original }) => {
            return (
              <div>
                <Button
                  content="Complete"
                  size="tiny"
                  onClick={e => this.onCompleteBtnClick(e, original)}
                  disabled={this.props.disabled}
                />
              </div>
            );
          },
        },
      ],
    },
  ];

  handleChangePrimaryQuantity = (refData, newValue) => {
    this.handleChangeQuantity(
      refData,
      newValue,
      CreateEditPrepWorksheetEnum.PreparedPrimary
    );
  };

  handleChangeSecondaryQuantity = (refData, newValue) => {
    this.handleChangeQuantity(
      refData,
      newValue,
      CreateEditPrepWorksheetEnum.PreparedSecondary
    );
  };

  handleChangeQuantity = (refData, newValue, quantity) => {
    const { negativeQuantityAllowed } = this.props;
    newValue = negativeQuantityAllowed
      ? newValue.match(/^-?\d{0,6}(\.\d{0,4})?/)[0]
      : newValue.match(/\d{0,6}(\.\d{0,4})?/)[0];
    const formValues =
      this.props.input.value && this.props.input.value.length
        ? _.cloneDeep(this.props.input.value)
        : [];
    const index = _.findIndex(formValues, formValue => {
      return formValue.ItemCode === refData.ItemCode;
    });
    if (index > -1) {
      formValues[index][quantity] = newValue;
    } else {
      const newFiled = Object.assign({}, refData);
      newFiled[quantity] = newValue;
      if (newValue.trim().length) {
        formValues.push(newFiled);
      }
    }
    this.props.input.onChange(formValues);
  };

  prepareTableData = () => {
    const dataSourceCopy =
      this.props.dataSource && this.props.dataSource.length
        ? _.cloneDeep(this.props.dataSource)
        : [];
    const { value } = this.props.input;
    let itemsList = [];
    if (value && value.length && dataSourceCopy && dataSourceCopy.length) {
      _.each(value, item => {
        const index = _.findIndex(dataSourceCopy, {
          [CreateEditPrepWorksheetEnum.ItemCode]:
            item[CreateEditPrepWorksheetEnum.ItemCode],
        });
        if (index > -1) {
          const dataRow = { ...item };
          dataSourceCopy[index][CreateEditPrepWorksheetEnum.PreparedPrimary] =
            dataRow[CreateEditPrepWorksheetEnum.PreparedPrimary];
          dataSourceCopy[index][CreateEditPrepWorksheetEnum.PreparedSecondary] =
            dataRow[CreateEditPrepWorksheetEnum.PreparedSecondary];
          if (
            dataRow[CreateEditPrepWorksheetEnum.PreparedPrimary] <
            dataRow[CreateEditPrepWorksheetEnum.NeededPrimary]
          ) {
            itemsList.push(
              dataRow[CreateEditPrepWorksheetEnum.ItemCode] +
                '-' +
                dataRow[CreateEditPrepWorksheetEnum.Description]
            );
          }
        }
      });
    }
    this.props.setNeededPreparedQtyDiff(itemsList);
    return dataSourceCopy;
  };

  onCompleteBtnClick = (e, original) => {
    let formValues =
      this.props.kitchenPrepWorksheetInfo &&
      _.cloneDeep(this.props.kitchenPrepWorksheetInfo);
    const index = _.findIndex(formValues, formValue => {
      return formValue.ItemCode === original.ItemCode;
    });
    if (index > -1) {
      formValues[index][CreateEditPrepWorksheetEnum.PreparedPrimary] =
        original[CreateEditPrepWorksheetEnum.NeededPrimary];
      formValues[index][CreateEditPrepWorksheetEnum.PreparedSecondary] =
        original[CreateEditPrepWorksheetEnum.NeededSecondary];
    } else {
      const orgItem = { ...original };
      orgItem[CreateEditPrepWorksheetEnum.PreparedPrimary] =
        original[CreateEditPrepWorksheetEnum.NeededPrimary];
      orgItem[CreateEditPrepWorksheetEnum.PreparedSecondary] =
        original[CreateEditPrepWorksheetEnum.NeededSecondary];
      formValues.push(orgItem);
    }
    this.props.input.onChange(formValues);
  };

  render() {
    const {
      sorted,
      onSortedChange,
      filtered,
      onFilteredChange,
      page,
      onPageChange,
      loading,
    } = this.props;

    return (
      <Form.Field>
        <ReactTable
          defaultPageSize={20}
          showPageSizeOptions={true}
          minRows={0}
          data={this.prepareTableData()}
          noDataText={loading ? '' : 'No items found'}
          loadingText={loading ? 'Loading...' : ''}
          resizable={false}
          className="-striped -highlight modalGrid"
          columns={this.columns}
          loading={loading}
          defaultFilterMethod={caseInsensitiveFilter}
          sorted={sorted}
          onSortedChange={onSortedChange}
          filtered={filtered}
          onFilteredChange={onFilteredChange}
          ref={this.reactTableRef}
          page={page}
          onPageChange={onPageChange}
          saveState={{
            tableName:
              TableNamesEnum.CK_KITCHEN_PREP_WORKSHEET_CREATE_EDIT_TABLE,
          }}
        />
      </Form.Field>
    );
  }
}

const selector = formValueSelector(
  CK_KITCHEN_PREP_WORKSHEET_CREATE_EDIT_FORM_NAME
);
const mapStateToProps = state => {
  return {
    kitchenPrepWorksheetInfo: selector(state, [
      CreateEditPrepWorksheetEnum.KitchenPrepWorksheetInfo,
    ]),
  };
};

const mapDispatchToProps = {
  ...actions,
  forceDirtyState,
};
export const KitchenPrepWorksheetInfo = connect(
  mapStateToProps,
  mapDispatchToProps
)(KitchenPrepWorksheetInfoContainer);



// WEBPACK FOOTER //
// ./src/pages/centralkitchen/kitchenprepworksheet/containers/createEditKitchenPrepWorksheet/KitchenPrepWorksheetInfo.js